import React, { useState, useMemo } from 'react';
import {
  View,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Alert,
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  Card,
  useTheme,
  ActivityIndicator,
  IconButton,
  Menu,
} from 'react-native-paper';
import { Avatar } from '@coffeeplexity/ui';
import {
  MessageCircle,
  Heart,
  Reply,
  Send,
  MoreVertical,
  Edit3,
  Trash2,
} from 'lucide-react-native';
import {
  useRecipeComments,
  useCreateComment,
  useReplyToComment,
  useLikeComment,
  useUnlikeComment,
  useUpdateComment,
  useDeleteComment,
} from '@coffeeplexity/api-client';
import { useAppSelector } from '../../store';
import { logger } from '../../utils/logger';
import { useProfileNavigation } from '../../hooks/useProfileNavigation';

// Interface matching the API response structure roughly
interface ApiComment {
  id: string;
  user_id: string;
  user_name: string;
  user_avatar?: string;
  text: string;
  created_at: string;
  likes_count: number;
  is_liked_by_user: boolean;
  replies_count: number;
  parent_id?: string;
  role?: string;
}

// Interface for the UI tree structure
interface CommentNode extends ApiComment {
  replies: CommentNode[];
}

interface RecipeCommentsProps {
  recipeId: string;
}

export function RecipeComments({ recipeId }: RecipeCommentsProps) {
  const theme = useTheme();
  const { user } = useAppSelector(state => state.auth);
  const { navigateToProfile } = useProfileNavigation();

  // Queries and mutations
  const { data, isLoading } = useRecipeComments(recipeId);
  const createComment = useCreateComment();
  const replyToComment = useReplyToComment();
  const likeComment = useLikeComment();
  const unlikeComment = useUnlikeComment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();

  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [menuVisible, setMenuVisible] = useState<string | null>(null);

  // Transform flat list to tree structure
  const commentsTree = useMemo(() => {
    if (!data?.comments) return [];

    // The API might return different field names, let's map safely
    const rawComments = data.comments;
    const commentMap = new Map<string, CommentNode>();
    const roots: CommentNode[] = [];

    // Initialize map
    rawComments.forEach((c: any) => {
      // Map API fields to our internal structure
      const comment: CommentNode = {
        id: c.id,
        user_id: c.user_id,
        user_name: c.user_name || 'User',
        user_avatar: c.user_avatar,
        text: c.text || c.content, // Handle 'text' or 'content' field
        created_at: c.created_at,
        likes_count: c.likes_count || 0,
        is_liked_by_user: c.is_liked_by_user || false,
        replies_count: c.replies_count || 0,
        parent_id: c.parent_id || c.parent_comment_id,
        role: c.user_role, // Map user_role from API to role
        replies: [],
      };
      commentMap.set(comment.id, comment);
    });

    // Build tree
    commentMap.forEach(node => {
      if (node.parent_id && commentMap.has(node.parent_id)) {
        const parent = commentMap.get(node.parent_id)!;
        parent.replies.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort by date (newest first)
    roots.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    // Sort replies (oldest first)
    commentMap.forEach(node => {
      node.replies.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });

    return roots;
  }, [data?.comments]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;

    try {
      await createComment.mutateAsync({ recipeId, content: newComment });
      setNewComment('');
    } catch (error) {
      logger.error('Failed to submit comment:', error);
    }
  };

  const handleSubmitReply = async (commentId: string) => {
    if (!replyContent.trim()) return;

    try {
      await replyToComment.mutateAsync({
        recipeId,
        commentId,
        content: replyContent,
      });
      setReplyContent('');
      setReplyingTo(null);
    } catch (error) {
      logger.error('Failed to submit reply:', error);
    }
  };

  const handleLikeComment = async (commentId: string, isLiked: boolean) => {
    try {
      if (isLiked) {
        await unlikeComment.mutateAsync(commentId);
      } else {
        await likeComment.mutateAsync(commentId);
      }
    } catch (error) {
      logger.error('Failed to toggle like:', error);
    }
  };

  const handleEditComment = (comment: CommentNode) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.text);
    setMenuVisible(null);
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!editContent.trim()) return;

    try {
      await updateComment.mutateAsync({ commentId, content: editContent });
      setEditingCommentId(null);
      setEditContent('');
    } catch (error) {
      logger.error('Failed to update comment:', error);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setMenuVisible(null);
    Alert.alert(
      'Delete Comment',
      'Are you sure you want to delete this comment? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteComment.mutateAsync(commentId);
            } catch (error) {
              logger.error('Failed to delete comment:', error);
            }
          },
        },
      ]
    );
  };

  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor(
        (now.getTime() - date.getTime()) / (1000 * 60 * 60)
      );

      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      return date.toLocaleDateString();
    } catch (e) {
      return '';
    }
  };

  const renderComment = (comment: CommentNode, isReply = false) => {
    const isOwner = user?.id === comment.user_id;
    const isEditing = editingCommentId === comment.id;

    return (
      <View
        key={comment.id}
        style={[styles.commentContainer, isReply && styles.replyContainer]}
      >
        <View style={styles.commentHeader}>
          <TouchableOpacity
            onPress={() =>
              navigateToProfile(comment.user_id, (comment as any).role)
            }
          >
            <Avatar
              size='xs'
              source={
                comment.user_avatar ? { uri: comment.user_avatar } : undefined
              }
              name={comment.user_name}
            />
          </TouchableOpacity>
          <View style={styles.commentMeta}>
            <TouchableOpacity
              onPress={() =>
                navigateToProfile(comment.user_id, (comment as any).role)
              }
            >
              <Text variant='bodyMedium' style={styles.userName}>
                {comment.user_name}
              </Text>
            </TouchableOpacity>
            <Text
              variant='bodySmall'
              style={[styles.timeAgo, { color: theme.colors.onSurfaceVariant }]}
            >
              {formatTimeAgo(comment.created_at)}
            </Text>
          </View>

          {/* Show menu for comment owner */}
          {isOwner && (
            <Menu
              visible={menuVisible === comment.id}
              onDismiss={() => setMenuVisible(null)}
              anchor={
                <IconButton
                  icon={() => <MoreVertical size={16} />}
                  size={16}
                  onPress={() => setMenuVisible(comment.id)}
                />
              }
            >
              <Menu.Item
                onPress={() => handleEditComment(comment)}
                title='Edit'
                leadingIcon={() => <Edit3 size={16} />}
              />
              <Menu.Item
                onPress={() => handleDeleteComment(comment.id)}
                title='Delete'
                leadingIcon={() => <Trash2 size={16} />}
                titleStyle={{ color: theme.colors.error }}
              />
            </Menu>
          )}
        </View>

        {/* Edit mode */}
        {isEditing ? (
          <View style={styles.editForm}>
            <TextInput
              mode='outlined'
              value={editContent}
              onChangeText={setEditContent}
              placeholder='Edit your comment...'
              multiline
              dense
            />
            <View style={styles.editActions}>
              <Button
                onPress={() => {
                  setEditingCommentId(null);
                  setEditContent('');
                }}
              >
                Cancel
              </Button>
              <Button
                onPress={() => handleUpdateComment(comment.id)}
                disabled={!editContent.trim() || updateComment.isPending}
                loading={updateComment.isPending}
              >
                Save
              </Button>
            </View>
          </View>
        ) : (
          <Text
            variant='bodyMedium'
            style={[styles.commentContent, { color: theme.colors.onSurface }]}
          >
            {comment.text}
          </Text>
        )}

        <View style={styles.commentActions}>
          <Button
            mode='text'
            onPress={() =>
              handleLikeComment(comment.id, comment.is_liked_by_user)
            }
            icon={({ size, color }) => (
              <Heart
                size={size}
                color={comment.is_liked_by_user ? theme.colors.error : color}
                fill={comment.is_liked_by_user ? theme.colors.error : 'none'}
              />
            )}
            textColor={
              comment.is_liked_by_user
                ? theme.colors.error
                : theme.colors.onSurfaceVariant
            }
            compact
          >
            {comment.likes_count}
          </Button>

          {!isReply && (
            <Button
              mode='text'
              onPress={() => setReplyingTo(comment.id)}
              icon={({ size, color }) => <Reply size={size} color={color} />}
              textColor={theme.colors.onSurfaceVariant}
              compact
            >
              Reply
            </Button>
          )}
        </View>

        {/* Reply form */}
        {replyingTo === comment.id && (
          <View style={styles.replyForm}>
            <TextInput
              mode='outlined'
              value={replyContent}
              onChangeText={setReplyContent}
              placeholder='Write a reply...'
              multiline
              dense
              right={
                <TextInput.Icon
                  icon={({ size, color }) => <Send size={size} color={color} />}
                  onPress={() => handleSubmitReply(comment.id)}
                  disabled={!replyContent.trim() || replyToComment.isPending}
                />
              }
            />
            <Button
              mode='text'
              onPress={() => {
                setReplyingTo(null);
                setReplyContent('');
              }}
              textColor={theme.colors.onSurfaceVariant}
              compact
            >
              Cancel
            </Button>
          </View>
        )}

        {/* Render replies */}
        {comment.replies.map(reply => renderComment(reply, true))}
      </View>
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.header}>
        <View style={styles.headerTitle}>
          <MessageCircle size={20} color={theme.colors.onSurfaceVariant} />
          <Text variant='titleMedium' style={styles.title}>
            Comments ({data?.total || 0})
          </Text>
        </View>

        {/* Comment form */}
        <Card
          style={[
            styles.commentForm,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <Card.Content>
            <View style={styles.formHeader}>
              <Avatar
                size='xs'
                name={user?.username || 'User'}
                source={
                  (user as any)?.profile?.avatar_url
                    ? { uri: (user as any).profile.avatar_url }
                    : undefined
                }
              />
              <Text
                variant='bodyMedium'
                style={[styles.formLabel, { color: theme.colors.onSurface }]}
              >
                Share your thoughts...
              </Text>
            </View>
            <TextInput
              mode='outlined'
              value={newComment}
              onChangeText={setNewComment}
              placeholder='What do you think about this recipe?'
              multiline
              numberOfLines={3}
              style={styles.commentInput}
            />
            <View style={styles.formActions}>
              <Button
                mode='contained'
                onPress={handleSubmitComment}
                disabled={!newComment.trim() || createComment.isPending}
                loading={createComment.isPending}
                icon={({ size, color }) => <Send size={size} color={color} />}
              >
                Comment
              </Button>
            </View>
          </Card.Content>
        </Card>
      </View>

      {/* Comments List */}
      <View style={styles.commentsList}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
          </View>
        ) : commentsTree.length > 0 ? (
          commentsTree.map(comment => renderComment(comment))
        ) : (
          <View style={styles.emptyState}>
            <MessageCircle size={48} color={theme.colors.onSurfaceVariant} />
            <Text
              variant='titleMedium'
              style={[
                styles.emptyTitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              No comments yet
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Be the first to share your thoughts!
            </Text>
          </View>
        )}
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  commentsList: {
    marginTop: 8,
  },
  header: {
    marginBottom: 8,
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  title: {
    fontWeight: '600',
  },
  commentForm: {
    marginBottom: 16,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  formLabel: {
    fontWeight: '500',
  },
  commentInput: {
    marginBottom: 12,
  },
  formActions: {
    alignItems: 'flex-end',
  },
  commentContainer: {
    marginBottom: 16,
  },
  replyContainer: {
    marginLeft: 32,
    marginTop: 12,
    marginBottom: 4,
  },
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 12,
  },
  commentMeta: {
    flex: 1,
  },
  userName: {
    fontWeight: '600',
  },
  timeAgo: {
    fontSize: 12,
  },
  commentContent: {
    marginBottom: 8,
    lineHeight: 20,
  },
  commentActions: {
    flexDirection: 'row',
    gap: 8,
  },
  replyForm: {
    marginTop: 12,
    gap: 8,
  },
  editForm: {
    marginBottom: 12,
    gap: 8,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  emptyState: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyTitle: {
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    textAlign: 'center',
  },
  loadingContainer: {
    padding: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
